/**
 * Offline State Context
 * 
 * Provides centralized state management for offline-first functionality including
 * network status, sync status, data availability, and offline operations.
 */

import React, { createContext, useReducer, useEffect, ReactNode } from 'react';
import type { SyncStatus } from '@/services/offline/sync';

/**
 * Offline state interface
 */
export interface OfflineState {
  // Network and connectivity
  isOnline: boolean;
  isConnected: boolean;
  connectionQuality: 'good' | 'poor' | 'offline';
  
  // Sync status
  syncStatus: SyncStatus | null;
  isSyncing: boolean;
  lastSyncTime: string | null;
  syncError: string | null;
  
  // Data availability
  hasLocalData: boolean;
  isDataStale: boolean;
  dataLastUpdated: string | null;
  
  // Queue status
  pendingOperations: number;
  failedOperations: number;
  conflictCount: number;
  
  // UI state
  showOfflineIndicator: boolean;
  showSyncProgress: boolean;
  syncProgress: {
    current: number;
    total: number;
    stage: string;
  } | null;
  
  // Loading states
  isInitializing: boolean;
  isLoadingData: boolean;
  
  // Error states
  initializationError: string | null;
  dataError: string | null;
}

/**
 * Offline state actions
 */
export type OfflineAction =
  | { type: 'SET_ONLINE_STATUS'; payload: boolean }
  | { type: 'SET_CONNECTION_QUALITY'; payload: 'good' | 'poor' | 'offline' }
  | { type: 'SET_SYNC_STATUS'; payload: SyncStatus }
  | { type: 'SET_SYNCING'; payload: boolean }
  | { type: 'SET_SYNC_ERROR'; payload: string | null }
  | { type: 'SET_SYNC_PROGRESS'; payload: { current: number; total: number; stage: string } | null }
  | { type: 'SET_LOCAL_DATA_STATUS'; payload: { hasData: boolean; isStale: boolean; lastUpdated: string | null } }
  | { type: 'SET_QUEUE_STATUS'; payload: { pending: number; failed: number; conflicts: number } }
  | { type: 'SET_UI_STATE'; payload: Partial<Pick<OfflineState, 'showOfflineIndicator' | 'showSyncProgress'>> }
  | { type: 'SET_LOADING_STATE'; payload: Partial<Pick<OfflineState, 'isInitializing' | 'isLoadingData'>> }
  | { type: 'SET_ERROR_STATE'; payload: Partial<Pick<OfflineState, 'initializationError' | 'dataError'>> }
  | { type: 'RESET_STATE' };

/**
 * Initial offline state
 */
const initialState: OfflineState = {
  // Network and connectivity
  isOnline: navigator.onLine,
  isConnected: navigator.onLine,
  connectionQuality: navigator.onLine ? 'good' : 'offline',
  
  // Sync status
  syncStatus: null,
  isSyncing: false,
  lastSyncTime: null,
  syncError: null,
  
  // Data availability
  hasLocalData: false,
  isDataStale: false,
  dataLastUpdated: null,
  
  // Queue status
  pendingOperations: 0,
  failedOperations: 0,
  conflictCount: 0,
  
  // UI state
  showOfflineIndicator: false,
  showSyncProgress: false,
  syncProgress: null,
  
  // Loading states
  isInitializing: true,
  isLoadingData: false,
  
  // Error states
  initializationError: null,
  dataError: null
};

/**
 * Offline state reducer
 */
function offlineStateReducer(state: OfflineState, action: OfflineAction): OfflineState {
  switch (action.type) {
    case 'SET_ONLINE_STATUS':
      return {
        ...state,
        isOnline: action.payload,
        isConnected: action.payload,
        connectionQuality: action.payload ? 'good' : 'offline',
        showOfflineIndicator: !action.payload
      };

    case 'SET_CONNECTION_QUALITY':
      return {
        ...state,
        connectionQuality: action.payload,
        isConnected: action.payload !== 'offline'
      };

    case 'SET_SYNC_STATUS':
      return {
        ...state,
        syncStatus: action.payload,
        isSyncing: action.payload.isSyncing,
        lastSyncTime: action.payload.lastSyncTime,
        pendingOperations: action.payload.pendingOperations,
        failedOperations: action.payload.failedOperations,
        conflictCount: action.payload.conflictCount,
        syncProgress: action.payload.progress
      };

    case 'SET_SYNCING':
      return {
        ...state,
        isSyncing: action.payload,
        showSyncProgress: action.payload
      };

    case 'SET_SYNC_ERROR':
      return {
        ...state,
        syncError: action.payload
      };

    case 'SET_SYNC_PROGRESS':
      return {
        ...state,
        syncProgress: action.payload,
        showSyncProgress: action.payload !== null
      };

    case 'SET_LOCAL_DATA_STATUS':
      return {
        ...state,
        hasLocalData: action.payload.hasData,
        isDataStale: action.payload.isStale,
        dataLastUpdated: action.payload.lastUpdated
      };

    case 'SET_QUEUE_STATUS':
      return {
        ...state,
        pendingOperations: action.payload.pending,
        failedOperations: action.payload.failed,
        conflictCount: action.payload.conflicts
      };

    case 'SET_UI_STATE':
      return {
        ...state,
        ...action.payload
      };

    case 'SET_LOADING_STATE':
      return {
        ...state,
        ...action.payload
      };

    case 'SET_ERROR_STATE':
      return {
        ...state,
        ...action.payload
      };

    case 'RESET_STATE':
      return initialState;

    default:
      return state;
  }
}

/**
 * Context interface
 */
interface OfflineContextType {
  state: OfflineState;
  dispatch: React.Dispatch<OfflineAction>;

  // Action helpers
  setOnlineStatus: (isOnline: boolean) => void;
  setConnectionQuality: (quality: 'good' | 'poor' | 'offline') => void;
  setSyncStatus: (status: SyncStatus) => void;
  setSyncing: (isSyncing: boolean) => void;
  setSyncError: (error: string | null) => void;
  setSyncProgress: (progress: { current: number; total: number; stage: string } | null) => void;
  setLocalDataStatus: (hasData: boolean, isStale: boolean, lastUpdated: string | null) => void;
  setQueueStatus: (pending: number, failed: number, conflicts: number) => void;
  setLoadingState: (loading: Partial<Pick<OfflineState, 'isInitializing' | 'isLoadingData'>>) => void;
  setErrorState: (errors: Partial<Pick<OfflineState, 'initializationError' | 'dataError'>>) => void;
  resetState: () => void;
  triggerSync: () => Promise<void>;
}

/**
 * Create context
 */
const OfflineStateContext = createContext<OfflineContextType | undefined>(undefined);

/**
 * Context provider props
 */
interface OfflineStateProviderProps {
  children: ReactNode;
}

/**
 * Offline State Provider
 */
export function OfflineStateProvider({ children }: OfflineStateProviderProps) {
  const [state, dispatch] = useReducer(offlineStateReducer, initialState);

  // Action helpers
  const setOnlineStatus = (isOnline: boolean) => {
    dispatch({ type: 'SET_ONLINE_STATUS', payload: isOnline });
  };

  const setConnectionQuality = (quality: 'good' | 'poor' | 'offline') => {
    dispatch({ type: 'SET_CONNECTION_QUALITY', payload: quality });
  };

  const setSyncStatus = (status: SyncStatus) => {
    dispatch({ type: 'SET_SYNC_STATUS', payload: status });
  };

  const setSyncing = (isSyncing: boolean) => {
    dispatch({ type: 'SET_SYNCING', payload: isSyncing });
  };

  const setSyncError = (error: string | null) => {
    dispatch({ type: 'SET_SYNC_ERROR', payload: error });
  };

  const setSyncProgress = (progress: { current: number; total: number; stage: string } | null) => {
    dispatch({ type: 'SET_SYNC_PROGRESS', payload: progress });
  };

  const setLocalDataStatus = (hasData: boolean, isStale: boolean, lastUpdated: string | null) => {
    dispatch({ 
      type: 'SET_LOCAL_DATA_STATUS', 
      payload: { hasData, isStale, lastUpdated } 
    });
  };

  const setQueueStatus = (pending: number, failed: number, conflicts: number) => {
    dispatch({ 
      type: 'SET_QUEUE_STATUS', 
      payload: { pending, failed, conflicts } 
    });
  };

  const setLoadingState = (loading: Partial<Pick<OfflineState, 'isInitializing' | 'isLoadingData'>>) => {
    dispatch({ type: 'SET_LOADING_STATE', payload: loading });
  };

  const setErrorState = (errors: Partial<Pick<OfflineState, 'initializationError' | 'dataError'>>) => {
    dispatch({ type: 'SET_ERROR_STATE', payload: errors });
  };

  const resetState = () => {
    dispatch({ type: 'RESET_STATE' });
  };

  const triggerSync = async () => {
    try {
      const { syncService } = await import('@/services/offline');
      await syncService.performSync();
    } catch (error) {
      console.error('Manual sync failed:', error);
      setSyncError(error instanceof Error ? error.message : 'Manual sync failed');
    }
  };

  // Initialize IndexedDB service only when needed (not on landing pages)
  useEffect(() => {
    const initializeOfflineServices = async () => {
      // Skip initialization on landing/public pages
      const isPublicPage = window.location.pathname === '/' ||
                          window.location.pathname.startsWith('/landing') ||
                          window.location.pathname.startsWith('/auth') ||
                          window.location.pathname.startsWith('/blog') ||
                          window.location.pathname.startsWith('/legal') ||
                          window.location.pathname.startsWith('/referral-landing');

      if (isPublicPage) {
        setLoadingState({ isInitializing: false });
        return;
      }

      try {
        const { indexedDBService } = await import('@/services/offline');
        await indexedDBService.initialize();
        setLoadingState({ isInitializing: false });
      } catch (error) {
        console.error('❌ Failed to initialize IndexedDB service:', error);
        setErrorState({ initializationError: 'Failed to initialize offline storage' });
        setLoadingState({ isInitializing: false });
      }
    };

    initializeOfflineServices();

    // Set up sync event listeners
    const setupSyncEventListeners = async () => {
      try {
        // Import sync service dynamically
        const { syncService } = await import('@/services/offline');

        const handleSyncEvent = (event: { type: string; data?: Record<string, unknown> }) => {
          switch (event.type) {
            case 'sync_started':
              setSyncing(true);
              setSyncError(null);
              break;

            case 'sync_progress':
              if (event.data && typeof event.data === 'object' && 'current' in event.data && 'total' in event.data && 'stage' in event.data) {
                setSyncProgress(event.data as { current: number; total: number; stage: string });
              }
              break;

            case 'sync_completed':
              setSyncing(false);
              setSyncError(null);
              setSyncProgress(null);
              setSyncStatus(syncService.getStatus());
              break;

            case 'sync_failed':
              setSyncing(false);
              setSyncError(event.data && typeof event.data === 'object' && 'error' in event.data ? String(event.data.error) : 'Sync failed');
              setSyncProgress(null);
              break;

            case 'network_status_changed':
              if (event.data && typeof event.data === 'object' && 'isOnline' in event.data) {
                setOnlineStatus(Boolean(event.data.isOnline));
              }
              break;

            case 'conflict_detected':
              setSyncStatus(syncService.getStatus());
              break;
          }
        };

        // Add sync service event listener
        syncService.addEventListener(handleSyncEvent);

        // Set up Capacitor background sync listeners (native platforms only)
        try {
          const { isNativeApp } = await import('@/utils/platformUtils');
          if (isNativeApp()) {
            const { capacitorBackgroundSyncService } = await import('@/services/offline/capacitor');

            const handleCapacitorSyncEvent = (event: { type: string; data?: Record<string, unknown> }) => {
              switch (event.type) {
                case 'background_sync_started':
                  setSyncing(true);
                  setSyncError(null);
                  break;
                case 'background_sync_completed':
                  setSyncing(false);
                  setSyncError(null);
                  break;
                case 'background_sync_failed':
                  setSyncing(false);
                  setSyncError(
                    event.data && typeof event.data === 'object' && 'error' in event.data
                      ? String(event.data.error)
                      : 'Background sync failed'
                  );
                  break;
                case 'network_status_changed':
                  if (event.data && typeof event.data === 'object' && 'connected' in event.data) {
                    const connected = Boolean(event.data.connected);
                    setOnlineStatus(connected);
                    setConnectionQuality(connected ? 'good' : 'offline');
                  }
                  break;
              }
            };

            capacitorBackgroundSyncService.addEventListener(handleCapacitorSyncEvent);

            // Return cleanup function that includes Capacitor listeners
            return () => {
              syncService.removeEventListener(handleSyncEvent);
              capacitorBackgroundSyncService.removeEventListener(handleCapacitorSyncEvent);
            };
          }
        } catch (error) {
          console.warn('Failed to set up Capacitor sync listeners:', error);
        }

        // Return cleanup function for sync service only
        return () => {
          syncService.removeEventListener(handleSyncEvent);
        };
      } catch (error) {
        console.error('Failed to set up sync event listeners:', error);
        return () => {}; // Return empty cleanup function
      }
    };

    // Set up event listeners and store cleanup function
    let cleanupSyncListeners: (() => void) | null = null;
    setupSyncEventListeners().then(cleanup => {
      cleanupSyncListeners = cleanup;
    });

    // Cleanup function
    return () => {
      if (cleanupSyncListeners) {
        cleanupSyncListeners();
      }
    };
  }, []);

  // Set up network status listeners
  useEffect(() => {
    const handleOnline = () => setOnlineStatus(true);
    const handleOffline = () => setOnlineStatus(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Set up connection quality monitoring (single instance for entire app)
  useEffect(() => {
    let monitoringInterval: NodeJS.Timeout | null = null;

    const testConnection = async () => {
      try {
        const start = Date.now();
        const response = await fetch('/api/health', {
          method: 'HEAD',
          signal: AbortSignal.timeout(5000)
        });
        const duration = Date.now() - start;

        if (response.ok) {
          if (duration < 1000) {
            setConnectionQuality('good');
          } else if (duration < 3000) {
            setConnectionQuality('poor');
          } else {
            setConnectionQuality('poor');
          }
        } else {
          setConnectionQuality('poor');
        }
      } catch (error) {
        setConnectionQuality('offline');
      }
    };

    const startMonitoring = () => {
      if (!state.isOnline) return;

      // Test immediately
      testConnection();

      // Then test every 30 seconds
      monitoringInterval = setInterval(testConnection, 30000);
    };

    const stopMonitoring = () => {
      if (monitoringInterval) {
        clearInterval(monitoringInterval);
        monitoringInterval = null;
      }
    };

    if (state.isOnline) {
      startMonitoring();
    } else {
      setConnectionQuality('offline');
      stopMonitoring();
    }

    return () => {
      stopMonitoring();
    };
  }, [state.isOnline]);

  const contextValue: OfflineContextType = {
    state,
    dispatch,
    setOnlineStatus,
    setConnectionQuality,
    setSyncStatus,
    setSyncing,
    setSyncError,
    setSyncProgress,
    setLocalDataStatus,
    setQueueStatus,
    setLoadingState,
    setErrorState,
    resetState,
    triggerSync
  };

  return (
    <OfflineStateContext.Provider value={contextValue}>
      {children}
    </OfflineStateContext.Provider>
  );
}

// Export context for use in hooks
export { OfflineStateContext };


